import { Card, CardBody, Tab, Nav, Spinner } from "react-bootstrap";
import './styles.css';
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer
} from "recharts";

const StatisticsChart = ({ title, data, customProps, prefix = "", loading, onTabChange, cardType, decimalPlaces = 2 }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("1");
    const [tabLoading, setTabLoading] = useState(false);
    const scrollContainerRef = useRef(null);

    const getDateRange = (tabKey) => {
        const today = dayjs();
        switch (tabKey) {
            case "1": // Today
                return {
                    fromDate: today.startOf('day').format(),
                    toDate: today.endOf('day').format()
                };
            case "2": // 7 days
                return {
                    fromDate: today.subtract(7, 'day').startOf('day').format(),
                    toDate: today.endOf('day').format()
                };
            case "3": // 30 days
                return {
                    fromDate: today.subtract(30, 'day').startOf('day').format(),
                    toDate: today.endOf('day').format()
                };
            case "4": // Total
                return {
                    fromDate: null,
                    toDate: null
                };
            default:
                return {
                    fromDate: today.startOf('day').format(),
                    toDate: today.endOf('day').format()
                };
        }
    };

    const handleTabChange = async (tabKey) => {
        if (tabKey === activeTab) return;

        setActiveTab(tabKey);
        if (onTabChange) {
            setTabLoading(true);
            const dateRange = getDateRange(tabKey);
            await onTabChange(cardType, dateRange, tabKey);
            setTabLoading(false);
        }
    };

    useEffect(() => {
        // Initial load
        if (onTabChange) {
            const dateRange = getDateRange("1");
            onTabChange(cardType, dateRange, "1");
        }
    }, []);

    useEffect(() => {
        // Scroll to rightmost end when data or tab changes
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
        }
    }, [data, activeTab]);

    const getChartData = () => {
        if (!data || !data.list) return [];
        return data.list.map(item => ({
            date: item.date,
            value: item.value
        }));
    };

    const getTotalValue = () => {
        if (!data) return "0.00";
        return (data.total || 0).toFixed(decimalPlaces);
    };

    return (
        <Card className="shadow" style={{ width: "640px", padding: "0px" }} {...customProps}>
            <CardBody className="p-4">
                <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
                    <Nav variant="pills" className="flex-row tab-list">
                        <Nav.Item>
                            <Nav.Link eventKey="1" className="me-1">{t("TODAY")}</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="2" className="me-1">7{t("DAYS")}</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="3" className="me-1">30{t("DAYS")}</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="4" className="me-1">{t("TOTAL")}</Nav.Link>
                        </Nav.Item>
                    </Nav>

                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">{title}</h5>
                        <div className="text-end">
                            <div className="text-muted small">Total</div>
                            <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#007bff" }}>
                                {prefix}{getTotalValue()}
                            </div>
                        </div>
                    </div>

                    <Tab.Content>
                        {loading || tabLoading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
                                <Spinner animation="border" size="sm" />
                            </div>
                        ) : (
                            <div style={{ display: "flex", width: "100%", height: "300px", marginTop: "10px" }}>
                                {/* Fixed Y-axis */}
                                <div style={{ width: "40px", height: "100%" }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={getChartData()}
                                            margin={{ top: 14, bottom: 45, left: 0, right: 0 }}
                                            syncId="statsChart"
                                        >
                                            <YAxis
                                                tick={{ fontSize: 12 }}
                                                tickFormatter={(value) => `${prefix}${value}`}
                                                width={40}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="value"
                                                stroke="transparent"
                                                strokeWidth={0}
                                                dot={false}
                                                isAnimationActive={false}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Scrollable X-axis and chart */}
                                <div
                                    ref={scrollContainerRef}
                                    style={{ overflowX: "auto", flex: 1 }}
                                >
                                    <div
                                        style={{
                                            width: `${Math.max(getChartData().length * 50, 500)}px`,
                                            height: "100%"
                                        }}
                                    >
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart
                                                key={`${cardType}-${activeTab}`}
                                                data={getChartData()}
                                                margin={{ top: 14, left: 0 }}
                                            >
                                                <XAxis
                                                    dataKey="date"
                                                    tick={{ fontSize: 12 }}
                                                    interval="preserveStartEnd"
                                                />
                                                <Tooltip
                                                    formatter={(value) => [`${prefix}${value}`, 'Value']}
                                                    labelFormatter={(label) => `Time: ${label}`}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="value"
                                                    stroke="#007bff"
                                                    strokeWidth={2}
                                                    dot={{ r: 2 }}
                                                    activeDot={{ r: 5 }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Tab.Content>
                </Tab.Container>
            </CardBody>
        </Card>
    );
};

export default StatisticsChart;
